import React, { useEffect } from 'react';
import {
  Box, VStack, Text, Flex, Icon, Divider, Button, useColorModeValue,
  Image, Avatar, HStack, CloseButton, IconButton, Drawer, DrawerOverlay, DrawerContent,
  useDisclosure, useBreakpointValue
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MdDashboard, MdLogout, MdMenu, MdBusinessCenter, MdShowChart,
  MdAssessment, MdGroup, MdFlag, MdInsights
} from 'react-icons/md';
import { FaChevronRight } from 'react-icons/fa';
import './DiretoriaGeralSidebar.css';

const SidebarItem = ({ icon, label, to, isActive }) => {
  const navigate = useNavigate();
  const activeBg = useColorModeValue('brand.lightBlue', 'rgba(25, 118, 210, 0.2)');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const activeTextColor = useColorModeValue('brand.primary', 'brand.secondary');
  const isMobile = useBreakpointValue({ base: true, lg: false });

  return (
    <Flex
      align="center"
      p={{ base: 4, lg: 3 }}
      mx={{ base: 0, lg: 2 }}
      borderRadius={{ base: "md", lg: "lg" }}
      role="group"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeTextColor : textColor}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        color: isActive ? activeTextColor : textColor,
      }}
      transition="all 0.2s"
      onClick={() => {
        to && navigate(to);
        if (isMobile && window.onCloseMobileSidebar) {
          window.onCloseMobileSidebar();
        }
      }}
    >
      <Icon 
        as={icon} 
        fontSize={{ base: "24px", lg: "20px" }}
        mr="3"
        color={isActive ? activeTextColor : 'gray.500'}
      />
      <Text fontSize={{ base: "md", lg: "sm" }} fontWeight={isActive ? "semibold" : "medium"}>{label}</Text>
      {isActive && (
        <Icon 
          as={FaChevronRight} 
          fontSize={{ base: "16px", lg: "14px" }}
          ml="auto" 
          color={activeTextColor}
        />
      )}
    </Flex>
  );
};

const DiretoriaGeralSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  // Make onClose available globally for mobile
  useEffect(() => {
    if (isMobile) {
      window.onCloseMobileSidebar = onClose;
    }
    return () => {
      if (window.onCloseMobileSidebar) {
        delete window.onCloseMobileSidebar;
      }
    };
  }, [isMobile, onClose]);

  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const bgSidebar = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const logoSrc = useColorModeValue(
    '/Assets/Logos/logo_mini.png',
    '/Assets/Logos/Logo-night.png'
  );

  const logout = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');

  const SidebarContent = () => (
    <Box 
      bg={bgSidebar} 
      w={{ base: "100%", lg: "250px" }}
      h="100vh" 
      py="5" 
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      position={{ base: "relative", lg: "fixed" }}
      left="0"
      top="0"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
      shadow={{ base: "none", lg: "lg" }}
      transition="all 0.2s"
      zIndex="1000"
    >
      <Flex 
        direction="column" 
        h="full" 
        justify="space-between"
      >
        <Box>
                    <Flex 
            mb="3" 
            px="75" 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Box maxW="100%">
              <Image 
                src={logoSrc}
                alt="Logo"
                maxH="75px"
                objectFit="contain"
              />
            </Box>
            {isMobile && (
              <CloseButton onClick={onClose} size="md" />
            )}
          </Flex>
          
          <HStack mb="6" px="5" spacing={3}>
            <Avatar 
              size="sm" 
              name={userInfo?.name || 'Diretor Geral'}
              src={userInfo?.avatar || ''}
              bg="red.500"
              color="white"
            />
            <Box>
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {userInfo?.name || 'Diretor Geral'}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.500"
                noOfLines={1}
              >
                Diretoria Geral
              </Text>
            </Box>
          </HStack>
          
          <Divider mb="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Menu Principal
          </Text>

          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDashboard} 
                label="Dashboard Executivo" 
                to="/diretoria-geral/dashboard" 
                isActive={isActive('/diretoria-geral/dashboard')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdInsights} 
                label="Visão Estratégica" 
                to="/diretoria-geral/strategic-view" 
                isActive={isActive('/diretoria-geral/strategic-view')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdShowChart} 
                label="Performance" 
                to="/diretoria-geral/performance" 
                isActive={isActive('/diretoria-geral/performance')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdGroup} 
                label="Equipes" 
                to="/diretoria-geral/teams" 
                isActive={isActive('/diretoria-geral/teams')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdBusinessCenter} 
                label="Projetos" 
                to="/diretoria-geral/projects" 
                isActive={isActive('/diretoria-geral/projects')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdFlag} 
                label="Metas" 
                to="/diretoria-geral/goals" 
                isActive={isActive('/diretoria-geral/goals')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdAssessment} 
                label="Relatórios Gerenciais" 
                to="/diretoria-geral/reports" 
                isActive={isActive('/diretoria-geral/reports')}
              />
            </Box>
          </VStack>
        </Box>

        <Box mt="auto" px="4" mb="4">
          <Button 
            leftIcon={<MdLogout />}
            onClick={logout}
            variant="outline"
            width="100%"
            justifyContent="flex-start"
            fontWeight="normal"
            size={{ base: "md", lg: "sm" }}
            py={{ base: 6, lg: 4 }}
          >
            Sair
          </Button>
          <Text 
            fontSize="xs" 
            color="gray.500" 
            textAlign="center" 
            mt="3"
          >
            © 2025 MICA Capital
          </Text>
        </Box>
      </Flex>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            aria-label="Menu"
            icon={<MdMenu />}
            position="fixed"
            top="4"
            left="4"
            zIndex="1000"
            colorScheme="red"
            onClick={onOpen}
            size="lg"
            display={{ base: "flex", lg: "none" }}
            boxShadow="md"
          />
          <Drawer
            isOpen={isOpen}
            placement="left"
            onClose={onClose}
            returnFocusOnClose={false}
          >
            <DrawerOverlay />
            <DrawerContent maxW="280px">
              <SidebarContent />
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <SidebarContent />
      )}
    </>
  );
};

export default DiretoriaGeralSidebar;
